import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, Variants } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, Mail, Sparkles } from "lucide-react";
import { Button as UIButton } from "../ui/button";
import { cn } from "@/lib/utils";
import SilkCanvas from "@/components/Silk";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.18 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

function Button({
  children,
  onClick,
  variant = "primary",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "ghost";
}) {
  const base =
    variant === "primary"
      ? "min-w-[9rem] max-w-full rounded-full bg-gradient-to-r from-[#4B2DDF] to-[#00B7B0] text-white font-semibold shadow-[0_8px_30px_rgba(0,183,176,0.35)] hover:shadow-[0_8px_36px_rgba(0,183,176,0.5)]"
      : "min-w-[9rem] max-w-full rounded-full border border-white/70 text-white font-semibold hover:bg-white/10";

  return (
    <UIButton
      className={cn(
        base,
        "px-6 py-3 text-center leading-snug whitespace-normal break-words transition-all duration-200 hover:-translate-y-0.5"
      )}
      onClick={onClick}
    >
      {children}
    </UIButton>
  );
}

/* Responsive typography & spacing */
function useTokens() {
  const [w, setW] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1440
  );

  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR, { passive: true });
    return () => window.removeEventListener("resize", onR);
  }, []);

  return useMemo(() => {
    if (w < 375) return { h: 32, p: 14, gap: 16 };
    if (w < 600) return { h: 38, p: 15, gap: 20 };
    if (w < 1024) return { h: 52, p: 16, gap: 24 };
    if (w < 1440) return { h: 62, p: 18, gap: 28 };
    return { h: 70, p: 20, gap: 30 };
  }, [w]);
}

/* Measure header height so hero overlaps perfectly under navbar */
function useHeaderMetrics() {
  const [metrics, setMetrics] = useState({ height: 88, bottomGap: 0 });

  useEffect(() => {
    const header =
      (document.getElementById("site-header") as HTMLElement | null) ||
      (document.querySelector("header") as HTMLElement | null);

    if (!header) return;

    const compute = () => {
      const cs = getComputedStyle(header);
      const mb = parseFloat(cs.marginBottom || "0");
      const pb = parseFloat(cs.paddingBottom || "0");
      setMetrics({ height: header.offsetHeight, bottomGap: mb + pb });
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(header);
    window.addEventListener("resize", compute, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, []);

  return metrics;
}

/* Delay Silk canvas on low-end devices / prefers-reduced-motion */
function useDeferredSilk(delay = 320) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq?.matches) return;

    const timer = window.setTimeout(() => setReady(true), delay);
    return () => window.clearTimeout(timer);
  }, [delay]);

  return ready;
}

export default function ConvoHero({ className }: { className?: string }) {
  const { t } = useTranslation("hero"); // ← this pulls from hero.json
  const navigate = useNavigate();
  const { h, p, gap } = useTokens();
  const { height: headerH, bottomGap } = useHeaderMetrics();
  const shouldRenderSilk = useDeferredSilk();

  const contentOffset = "clamp(20px, 14vh, 120px)";
  // title/subtitle each carry a second, separately-styled line joined by
  // "\n" in every locale's hero.json (checked en/es/fr/hi) — not a layout
  // hack, the translated copy is already shaped this way.
  const [titleLead, titleAccent] = t("title").split("\n");
  const [subtitleBody, tagline] = t("subtitle").split("\n");
  const taglineWords = (tagline || "").split("|").map((word) => word.trim()).filter(Boolean);

  return (
    <section
      className={cn("relative isolate overflow-hidden z-0", className)}
      style={{
        marginTop: -(headerH + bottomGap),
        minHeight: "100dvh",
        paddingTop: `calc(${headerH}px + ${contentOffset})`,
        paddingBottom: "clamp(80px, 12vh, 150px)",
        backgroundColor: "#0A0C16"
      }}
      data-animate="fade-in"
    >
      {/* Silk background, deferred, tinted toward the brand palette rather than neutral gray */}
      {shouldRenderSilk && (
        <div
          className="absolute inset-0 -z-20 pointer-events-none will-change-transform"
          style={{ transform: "translateZ(0)" }}
          aria-hidden
        >
          <SilkCanvas
            speed={3.25}
            scale={1.08}
            rotation={-0.3}
            noiseIntensity={0.4}
            color="#2E3A6B"
          />
        </div>
      )}

      {/* Soft brand-color glow behind the headline, for depth */}
      <div
        className="absolute left-1/2 top-[18%] -z-10 h-[520px] w-[820px] -translate-x-1/2 rounded-full blur-[120px]"
        style={{ background: "radial-gradient(closest-side, rgba(0,183,176,0.22), rgba(75,45,223,0.12), transparent 70%)" }}
        aria-hidden
      />

      {/* Dark readability overlay */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, rgba(5,6,15,0.85) 0%, rgba(7,8,18,0.78) 60%, rgba(10,12,22,0.65) 100%)"
        }}
        aria-hidden
      />

      {/* Main Content */}
      <motion.div
        className="relative m-auto flex w-[92%] max-w-[1100px] flex-col items-center text-center"
        style={{ minHeight: "calc(100% - 1px)", gap }}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Eyebrow badge */}
        <motion.div
          variants={itemVariants}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-accent backdrop-blur-sm"
        >
          <Sparkles size={14} className="shrink-0" aria-hidden="true" />
          {t("eyebrow")}
        </motion.div>

        {/* Title */}
        <motion.h1
          variants={itemVariants}
          style={{ fontSize: h, lineHeight: 1.04, fontWeight: 800 }}
          className="text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.35)] text-balance"
        >
          {titleLead}
          <br />
          <span className="bg-gradient-to-r from-[#5CE0D8] to-[#8B7CF6] bg-clip-text text-transparent">{titleAccent}</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          variants={itemVariants}
          style={{ marginTop: 16, lineHeight: 1.75, fontSize: p }}
          className="max-w-[820px] text-white/75 text-balance"
        >
          {subtitleBody}
        </motion.p>

        {/* Tagline, as a row of pill-separated words rather than plain "|" text */}
        {taglineWords.length > 0 && (
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-semibold uppercase tracking-[0.18em] text-accent"
          >
            {taglineWords.map((word, i) => (
              <span key={word} className="flex items-center gap-3">
                {i > 0 && <span className="h-1 w-1 rounded-full bg-accent/70" aria-hidden="true" />}
                {word}
              </span>
            ))}
          </motion.div>
        )}

        {/* CTAs */}
        <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Button variant="primary" onClick={() => navigate("/signup")}>
            <span className="inline-flex items-center gap-2">{t("tryDemo")}<ArrowRight size={16}/></span>
          </Button>
          <a href="mailto:support-convobuddy@kirusa.com">
            <Button variant="ghost">
              <span className="inline-flex items-center gap-2"><Mail size={16}/>{t("contactSales")}</span>
            </Button>
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
